import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useStickyBulletin, type SectionId } from "../hooks/useStickyBulletin";
import { BulletinInboxTab } from "./bulletin/BulletinInboxTab";
import { FadingRuler } from "./shared/FadingRuler";
import {
  BulletinHeader,
  BulletinSectionNavigation,
} from "./sticky-bulletin/BulletinChrome";
import { BulletinNotesTab } from "./sticky-bulletin/BulletinNotesTab";
import { BulletinVolWatchTab } from "./sticky-bulletin/BulletinVolWatchTab";
import { UpcomingBulletinTab } from "./sticky-bulletin/UpcomingBulletinTab";
import { useBulletinSurface } from "./sticky-bulletin/use-bulletin-surface";

interface StickyBulletinProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  variant?: "desktop-popover" | "mobile-dropdown";
  initialSection?: SectionId;
}

export function StickyBulletin({
  open,
  onClose,
  anchorRef,
  variant = "desktop-popover",
  initialSection,
}: StickyBulletinProps) {
  const bulletin = useStickyBulletin(open, anchorRef);
  const isMobile = variant === "mobile-dropdown";
  const surface = useBulletinSurface({
    open,
    isMobile,
    onClose,
    popupPosition: bulletin.popupPos,
  });

  useEffect(() => {
    if (open && initialSection) bulletin.setActiveSection(initialSection);
  }, [initialSection, open]);

  if (!open) return null;
  if (!isMobile && !surface.panelPosition) return null;

  return createPortal(
    <div
      ref={isMobile ? undefined : bulletin.panelRef}
      data-bulletin-drawer-root={isMobile ? "mobile" : undefined}
      data-dismissing={surface.isDismissing || undefined}
      style={surface.rootStyle}
      className={
        isMobile
          ? "sticky-bulletin-mobile-drawer-root"
          : "w-[440px] max-w-[calc(100vw-24px)] animate-in fade-in slide-in-from-top-2 duration-200"
      }
      onMouseMove={surface.moveDesktopDrag}
      onMouseUp={surface.finishDesktopDrag}
      onMouseLeave={surface.finishDesktopDrag}
    >
      {isMobile ? (
        <button
          type="button"
          className="sticky-bulletin-mobile-backdrop"
          aria-label="Close bulletin"
          onClick={surface.requestClose}
        />
      ) : null}
      <div
        ref={isMobile ? bulletin.panelRef : undefined}
        data-bulletin-panel
        role={isMobile ? "dialog" : undefined}
        aria-modal={isMobile ? "true" : undefined}
        aria-label={isMobile ? "Bulletin" : undefined}
        className={
          isMobile
            ? "sticky-bulletin-mobile-sheet solvys-liquid-glass rounded-t-[30px]"
            : "overflow-hidden rounded-xl bg-[color-mix(in_srgb,var(--fintheon-surface)_94%,transparent)] shadow-[0_8px_32px_rgba(0,0,0,0.45)] ring-1 ring-[var(--fintheon-accent)]/15 backdrop-blur-xl"
        }
        data-dragging={surface.isMobileDragging || undefined}
        data-dismissing={surface.isDismissing || undefined}
        style={surface.mobileStyle}
        onPointerDown={surface.handleMobilePointerDown}
        onPointerMove={surface.handleMobilePointerMove}
        onPointerUp={surface.finishMobilePointerDrag}
        onPointerCancel={surface.finishMobilePointerDrag}
      >
        {isMobile ? (
          <div className="sticky-bulletin-mobile-grabber mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--fintheon-muted)]/28" />
        ) : null}
        <BulletinHeader
          isMobile={isMobile}
          isQuickClockVisible={bulletin.showQuickClock}
          isQuickClockActive={bulletin.quickClockPulse}
          onQuickClock={bulletin.handleQuickClock}
          onClose={surface.requestClose}
          onBeginDrag={surface.beginDesktopDrag}
        />
        <FadingRuler className="opacity-50" />
        <BulletinSectionNavigation
          isMobile={isMobile}
          activeSection={bulletin.activeSection}
          onSelect={bulletin.setActiveSection}
        />
        <div
          className={`min-h-[200px] overflow-y-auto p-3 custom-scrollbar ${
            isMobile ? "max-h-[calc(100dvh-164px)]" : "max-h-[420px]"
          }`}
          data-bulletin-scroll
        >
          <BulletinContent
            section={bulletin.activeSection}
            bulletin={bulletin}
          />
        </div>
        <FadingRuler className="opacity-45" />
      </div>
    </div>,
    document.body,
  );
}

function BulletinContent({
  section,
  bulletin,
}: {
  section: SectionId;
  bulletin: ReturnType<typeof useStickyBulletin>;
}) {
  if (section === "inbox") return <BulletinInboxTab />;
  if (section === "upcoming") return <UpcomingBulletinTab />;
  if (section === "notes") {
    return (
      <BulletinNotesTab
        value={bulletin.tradingNotes}
        isEditing={bulletin.editingNotes}
        onChange={bulletin.handleNotesChange}
        onEdit={() => bulletin.setEditingNotes(true)}
        onStopEditing={() => bulletin.setEditingNotes(false)}
      />
    );
  }
  return (
    <BulletinVolWatchTab
      entries={bulletin.volWatchTimes}
      time={bulletin.newVolWatchTime}
      day={bulletin.newVolWatchDay}
      instrument={bulletin.newVolWatchInstrument}
      notes={bulletin.newVolWatchNotes}
      isQuickVisible={bulletin.showQuickClock}
      isQuickActive={bulletin.quickClockPulse}
      isHotTimesOpen={bulletin.showHotTimes}
      isHotTimesLoaded={bulletin.hotTimesLoaded}
      isHotTimesByDay={bulletin.hotTimesByDay}
      hotTimes={bulletin.hotTimes}
      selectedSymbol={bulletin.selectedSymbol.symbol}
      onTimeChange={bulletin.setNewVolWatchTime}
      onDayChange={bulletin.setNewVolWatchDay}
      onInstrumentChange={bulletin.setNewVolWatchInstrument}
      onNotesChange={bulletin.setNewVolWatchNotes}
      onToggleQuick={() => bulletin.setShowQuickClock(!bulletin.showQuickClock)}
      onQuickClock={bulletin.handleQuickClock}
      onToggleHotTimes={() => bulletin.setShowHotTimes(!bulletin.showHotTimes)}
      onToggleHotTimesByDay={() =>
        bulletin.setHotTimesByDay(!bulletin.hotTimesByDay)
      }
      onAdd={bulletin.handleAddVolWatch}
    />
  );
}
